import { getSuggestionStatusValue } from "@src/core/engine/technicalSelection/types";
import { isInlineValueUnit } from "@src/core/engine/units/constants";
import { useParseValidationResult } from "@src/core/engine/validationResults/useParseValidationResult";
import { TechnicalSelectionOutput as WorkerSuggestionOutput } from "@src/core/engine/workers/technicalSelection/schema";
import { useGetProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { max } from "radashi";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export type BuildSuggestionOutput = (
  translationKey: string,
  output: WorkerSuggestionOutput[] | undefined,
) => TechnicalSelectionOutput;

export type TechnicalSelectionOutput = {
  error?: string | null;
  formattedValue: string | null | undefined;
  id: string;
  status: number | undefined;
  translationKey: string;
  values: (number | string)[] | null | undefined;
};

export function useBuildSuggestionOutput() {
  const { t } = useTranslation("product");
  const { formatDescriptions } = useGetProductDescriptions();
  const parseValidationResult = useParseValidationResult({
    fieldsT: { prefix: "validation.technicalSelection" },
    maxDecimals: 2,
  });
  const { getUnitSymbol } = useUnitConversion();

  return useCallback(
    (
      translationKey: string,
      groupedOutputs?: WorkerSuggestionOutput[],
    ): TechnicalSelectionOutput => {
      const highestSeverityItem = max(
        groupedOutputs ?? [],
        (item) =>
          getSuggestionStatusValue(item.validationResult?.severity) ?? 0,
      );
      const physicalType = groupedOutputs?.[0]?.physicalType;
      const titleSymbol =
        !isInlineValueUnit(physicalType) && physicalType
          ? ` - ${getUnitSymbol(physicalType)}`
          : "";

      return {
        error: highestSeverityItem?.validationResult
          ? parseValidationResult(highestSeverityItem.validationResult)
          : null,
        formattedValue: formatDescriptions(
          groupedOutputs?.map((item) => ({
            ...item,
            value: item.value.toString(),
          })),
          {
            excludeSymbol: !isInlineValueUnit(physicalType),
            targetDecimals: 2,
          },
        ),
        id: highestSeverityItem?.id ?? "",
        status: getSuggestionStatusValue(
          highestSeverityItem?.validationResult?.severity,
        ),
        translationKey: `${t(translationKey)}${titleSymbol}`,
        values: groupedOutputs?.map(({ value }) => value) ?? null,
      };
    },
    [formatDescriptions, getUnitSymbol, parseValidationResult, t],
  );
}
